import { inject, Injectable, InjectionToken, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { take } from 'rxjs';
import { environment } from '@ups/xplat/core';
import { MsalService } from '@azure/msal-angular';
import { AuthService } from '@auth0/auth0-angular';
import { AuthFacade } from '../auth-facade';
import { LoginIssuer, LoginIssuerStore } from '../login-issuer.store';
import { InternalAuthenticationService } from '@ups/xplat/base/auth';

export type { LoginIssuer } from '../login-issuer.store';

export interface EntraLoginConfig {
  clientId?: string;
  authority?: string;
  audience?: string;
  /**
   * Email domains routed to Entra by the identifier-first login screen.
   * Defaults to the corporate domain. Stream 3 replaces this heuristic
   * with a real eligibility lookup once Users.EntraObjectId exists.
   */
  entraDomains?: string[];
}

const DEFAULT_ENTRA_DOMAINS = ['universalplant.com'];

/**
 * Entra (Azure AD) config the identifier-first login screen reads.
 * Defaults to the app environment's `entraId` block (populated from the
 * `EntraId:*` Azure App Config keys via the base environment). Tests and
 * apps with a non-standard setup can provide their own value.
 */
export const ENTRA_LOGIN_CONFIG = new InjectionToken<EntraLoginConfig>(
  'ENTRA_LOGIN_CONFIG',
  { factory: () => environment.entraId ?? {} }
);

export interface Auth0LoginConfig {
  clientId?: string | null;
}

/**
 * Auth0 config the login screen reads to decide whether UPS1 sign-in can
 * work here (#36633). Defaults to the app environment's `auth0` block, whose
 * clientId comes from `Auth0:ClientId@<app>`. The base environment sets it to
 * null, so an app with no Auth0 client reads as unavailable.
 */
export const AUTH0_LOGIN_CONFIG = new InjectionToken<Auth0LoginConfig>(
  'AUTH0_LOGIN_CONFIG',
  { factory: () => environment.auth0 ?? {} }
);

/**
 * Issuer discovery + dispatch behind the identifier-first login screen
 * (Tim, 2026-07-04: "users put email address in first, then if it is an
 * Entra login we proceed with it"). The Entra path uses the MSAL REDIRECT
 * flow (browser SPA pattern shared with project-hub / ryns-roost web),
 * NOT the ROPC flow in UpsAuthService — ROPC bypasses MFA/conditional
 * access and is reserved for service-account logins. The UPS1 path
 * delegates to the existing Auth0 redirect flow unchanged; it stays
 * supported indefinitely for users without AD accounts (field hands).
 */
@Injectable()
export class DualLoginUiService {
  private readonly entraConfig = inject(ENTRA_LOGIN_CONFIG);
  private readonly auth0Config = inject(AUTH0_LOGIN_CONFIG);
  private readonly msal = inject(MsalService, { optional: true });
  private readonly auth0 = inject(AuthService, { optional: true });
  private readonly internal = inject(InternalAuthenticationService, {
    optional: true,
  });
  private readonly issuerStore = inject(LoginIssuerStore);
  // Lazy: AuthFacade constructs the bound AuthenticationService, which specs
  // exercising only realm discovery don't provide. Resolved on first use.
  private readonly injector = inject(Injector);

  /**
   * MSAL redirect logins return to the INITIATING url — the login page —
   * not the redirectUri, so a successful Entra sign-in re-landed on
   * /login showing "Welcome back", and Continue just round-tripped
   * Microsoft SSO back to /login forever (the second half of #29713).
   * The login screen calls this on init: an already-authenticated session
   * is forwarded into the app. A logged-out visitor (logout lands here)
   * reports unauthenticated and stays on the form, so signing out keeps
   * working. Both AuthenticationService implementations emit only after
   * their SDK finishes initializing, so take(1) cannot read a premature
   * false.
   */
  forwardAuthenticatedUser(returnTarget = '/'): void {
    const facade = this.injector.get(AuthFacade);
    const router = this.injector.get(Router);
    facade.isAuthenticated$.pipe(take(1)).subscribe((authenticated) => {
      if (authenticated) {
        router.navigateByUrl(returnTarget);
      }
    });
  }

  /**
   * Entra sign-in is offered only when the app has a SPA registration
   * (clientId + authority in config) AND the host app registered the
   * MSAL providers. Anything less routes everyone to Auth0 instead of
   * dispatching a redirect that can only fail.
   */
  entraAvailable(): boolean {
    return !!(
      this.entraConfig?.clientId &&
      this.entraConfig?.authority &&
      this.msal
    );
  }

  /**
   * UPS1 (Auth0) sign-in is offered only when the app has an Auth0 client id
   * AND registered the Auth0 module (#36633). web-hr-packet and
   * web-employee-packet have no client, and dispatching there reached Auth0
   * with no client_id ("Missing required parameter: client_id").
   */
  ups1Available(): boolean {
    return !!(this.auth0Config?.clientId && this.auth0);
  }

  /**
   * A phone or non-corporate email routes to UPS1. With no Auth0 client but
   * InternalAuth registered (web-employee-packet, the new-hire app), the
   * internal sign-in card is the only path that works, so use it (#36633).
   */
  internalInsteadOfUps1(): boolean {
    return !this.ups1Available() && this.internalAvailable();
  }

  internalAvailable(): boolean {
    return Boolean(this.internal);
  }

  /**
   * Identifier-first realm discovery: which issuer should this email log
   * in with? Domain-based until stream 3's eligibility endpoint exists.
   * Unknown domains and anything unparseable route to Auth0 — the safe
   * default, since every current user can sign in there today.
   */
  discoverIssuer(email: string): LoginIssuer {
    if (!this.entraAvailable()) {
      return 'auth0';
    }
    const domain = email.trim().toLowerCase().split('@')[1];
    if (!domain) {
      return 'auth0';
    }
    const entraDomains = this.entraConfig.entraDomains ?? DEFAULT_ENTRA_DOMAINS;
    return entraDomains.some((d) => d.toLowerCase() === domain)
      ? 'entra'
      : 'auth0';
  }

  /**
   * The single login field accepts an email address OR a phone number
   * (Tim, 2026-07-05, #29713). Classification decides which flow starts:
   * emails run realm discovery (Entra vs Auth0), phones are always Auth0
   * (Entra accounts are UPN/email-based, so a phone can never be one).
   * Anything else is null so the screen shows a validation message
   * instead of dispatching a redirect that can only fail.
   */
  classifyIdentifier(identifier: string): 'email' | 'phone' | null {
    const value = identifier.trim();
    if (!value) {
      return null;
    }
    if (value.includes('@')) {
      // Loose email shape: something@domain.tld — realm discovery and the
      // IdP do the real validation, this only gates obvious non-emails.
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? 'email' : null;
    }
    const digits = this.normalizePhone(value).replace(/^\+/, '');
    // 7 digits covers local formats; 15 is the E.164 maximum.
    const phoneShaped = /^\d{7,15}$/.test(digits);
    // Reject strings with letters or other junk around the digits.
    const onlyPhoneChars = /^[+\d\s().-]+$/.test(value);
    return phoneShaped && onlyPhoneChars ? 'phone' : null;
  }

  /** Strip phone formatting for the Auth0 login hint; a leading + survives. */
  normalizePhone(identifier: string): string {
    const value = identifier.trim();
    const digits = value.replace(/\D/g, '');
    return value.startsWith('+') ? `+${digits}` : digits;
  }

  /**
   * Which issuer did this user last sign in with (#29709)? Returning
   * users skip the email step when this reports a value. A persisted
   * 'entra' choice only counts when Entra is still available in this app
   * (stale localStorage must not dispatch a redirect that can only fail);
   * 'auth0' likewise needs an Auth0 client in this app (#36633).
   */
  returningIssuer(): LoginIssuer | null {
    const persisted = this.issuerStore.get();
    if (persisted === 'entra') {
      return this.entraAvailable() ? 'entra' : null;
    }
    if (persisted === 'internal') {
      return this.internalAvailable() ? 'internal' : null;
    }
    if (persisted === 'auth0') {
      return this.ups1Available() ? 'auth0' : null;
    }
    return persisted;
  }

  /** The "use a different account" escape: forget the remembered issuer. */
  forgetIssuer(): void {
    this.issuerStore.clear();
  }

  /**
   * Start the Entra redirect. Scopes default to the API audience's
   * `.default` scope; per-app wiring may pass explicit named scopes
   * (project-hub pattern). The email travels as loginHint so Microsoft
   * pre-fills the account picker with the identity the user just typed.
   *
   * 🔴 **Does NOT persist the issuer.** It used to, right here, before the
   * redirect, which made the stored marker record INTENT rather than
   * success (#30531). When a redirect came back without producing an MSAL
   * account, the marker still said 'entra', `selectLoginIssuer` honoured
   * it, and the app committed to the Entra path with no account to mint a
   * token from: `acquireTokenSilent` threw `no_account_error`, the
   * interceptor sent the permission call unauthenticated, the API returned
   * 401 to an anonymous caller, and the user saw an empty permission set.
   * Because the marker was rewritten on every attempt, a fresh incognito
   * profile reproduced it identically, which is what ruled out stale
   * storage as the cause.
   *
   * The marker is now written in exactly one place, the `APP_INITIALIZER`
   * in `entra-msal.providers`, and only when `handleRedirectObservable`
   * yields an account. That makes a persisted 'entra' mean "a redirect
   * COMPLETED", which is the invariant `selectLoginIssuer` always assumed.
   */
  loginWithEntra(loginHint?: string, scopes?: string[]): void {
    const msal = this.msal;
    if (!msal || !this.entraAvailable()) {
      throw new Error(
        'Entra ID sign-in is not configured for this app (missing EntraId clientId/authority or MSAL providers).'
      );
    }
    const effectiveScopes =
      scopes ??
      (this.entraConfig.audience
        ? [`${this.entraConfig.audience}/.default`]
        : []);
    const effectiveHint = loginHint ?? this.issuerStore.getHint() ?? undefined;
    msal.loginRedirect({
      scopes: effectiveScopes,
      ...(effectiveHint
        ? { loginHint: effectiveHint }
        : { prompt: 'select_account' }),
    });
  }

  /**
   * The existing Auth0 universal-login redirect, unchanged.
   *
   * ⚠️ This one DOES still persist on dispatch, deliberately and
   * asymmetrically with the Entra path above: Auth0 has no success hook to
   * write the marker from (nothing on the Auth0 return path calls
   * `issuerStore.set`), so moving this write would leave returning Auth0
   * users unremembered. The Entra path could move it because its
   * `APP_INITIALIZER` already writes on success. Do not "make these
   * symmetric" without first giving Auth0 an equivalent success hook.
   */
  loginWithUps1(returnTarget = '/', loginHint?: string): void {
    if (!this.auth0) {
      throw new Error(
        'Auth0 sign-in is not available (AuthModule not registered in this app).'
      );
    }
    if (!this.ups1Available()) {
      throw new Error(
        'Sign-in with a UPS1 account is not set up for this app.'
      );
    }
    this.issuerStore.set('auth0');
    this.auth0.loginWithRedirect({
      appState: { target: returnTarget },
      ...(loginHint ? { login_hint: loginHint } : {}),
    });
  }
}
